import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/axios";
import { useTranslation } from "../context/LanguageContext";
import { FaSyringe, FaCheck, FaTimes, FaPlus, FaTrash } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../styles/common.css";
import "./AjouterSante.css";

export default function AjouterSante() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    troupeau: "",
    animal: "",
    type_intervention: "",
    statut: "non_traite",
    date: "",
    description: "",
    prochaine_intervention: "",
  });

  const [troupeaux, setTroupeaux] = useState([]);
  const [animaux, setAnimaux] = useState([]);
  const [stocksDisponibles, setStocksDisponibles] = useState([]);
  const [produitsUtilises, setProduitsUtilises] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({
    troupeau: "",
    animal: "",
    type_intervention: "",
    date: "",
  });
  const [touchedFields, setTouchedFields] = useState({
    troupeau: false,
    animal: false,
    type_intervention: false,
    date: false,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [troupeauxRes, animauxRes, stocksRes] = await Promise.all([
          api.get("/api/troupeaux/"),
          api.get("/api/animaux/"),
          api.get("/api/stocks-pharmacie/disponibles/"),
        ]);
        setTroupeaux(troupeauxRes.data);
        setAnimaux(animauxRes.data);
        setStocksDisponibles(stocksRes.data);
      } catch (error) {
        console.error("Erreur lors du chargement des données:", error);
      }
    };
    fetchData();
  }, []);

  const validateRequiredField = (name, value) => {
    if (!value || (typeof value === 'string' && value.trim() === "") || value === null) {
      return "Ce champ est obligatoire";
    }
    return "";
  };

  const validateField = (name, value) => {
    if (name === "troupeau" || name === "animal" || name === "type_intervention" || name === "date") {
      return validateRequiredField(name, value);
    }
    return "";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Si le troupeau change, réinitialiser l'animal sélectionné
    if (name === "troupeau") {
      setFormData({ ...formData, troupeau: value, animal: "" });
    } else {
      setFormData({ ...formData, [name]: value });
    }
    
    // Validation en temps réel si le champ a été touché
    if (touchedFields[name] || fieldErrors[name]) {
      const error = validateField(name, value);
      setFieldErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    
    // Marquer le champ comme touché
    setTouchedFields(prev => ({ ...prev, [name]: true }));
    
    // Valider le champ
    const error = validateField(name, value);
    setFieldErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Marquer tous les champs obligatoires comme touchés
    const newTouchedFields = {
      troupeau: true,
      animal: true,
      type_intervention: true,
      date: true,
    };
    setTouchedFields(newTouchedFields);
    
    // Valider tous les champs obligatoires
    const newFieldErrors = {
      troupeau: validateField("troupeau", formData.troupeau),
      animal: validateField("animal", formData.animal),
      type_intervention: validateField("type_intervention", formData.type_intervention),
      date: validateField("date", formData.date),
    };
    setFieldErrors(newFieldErrors);
    
    // Vérifier s'il y a des erreurs
    const hasErrors = Object.values(newFieldErrors).some(error => error !== "");
    
    if (hasErrors) {
      return; // Ne pas soumettre si il y a des erreurs
    }
    
    setShowConfirmModal(true);
  };

  const handleConfirmSubmit = async () => {
    setLoading(true);
    try {
      const dataToSend = {
        ...formData,
        produits_utilises: produitsUtilises.map(p => ({
          stock_id: p.stock_id,
          quantite_utilisee: parseFloat(p.quantite_utilisee),
          notes: p.notes || "",
        })),
      };
      await api.post(`/api/sante/`, dataToSend);
      setShowConfirmModal(false);
      toast.success("Fiche santé ajoutée avec succès !");
      setTimeout(() => {
        navigate("/sante");
      }, 500);
    } catch (error) {
      console.error("Erreur lors de l'ajout:", error);
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.message ||
                          Object.values(error.response?.data || {}).flat().join(", ") ||
                          "Une erreur s'est produite lors de l'ajout.";
      toast.error(errorMessage);
      setShowConfirmModal(false);
    } finally {
      setLoading(false);
    }
  };

  const handleAddProduit = () => {
    setProduitsUtilises([...produitsUtilises, {
      stock_id: "",
      quantite_utilisee: "",
      notes: "",
    }]);
  };

  const handleRemoveProduit = (index) => {
    setProduitsUtilises(produitsUtilises.filter((_, i) => i !== index));
  };

  const handleProduitChange = (index, field, value) => {
    const updated = [...produitsUtilises];
    updated[index] = { ...updated[index], [field]: value };
    setProduitsUtilises(updated);
  };

  const getStockLabel = (stockId) => {
    const stock = stocksDisponibles.find(s => s.id === parseInt(stockId));
    if (!stock) return "";
    return `${stock.produit_nom} - ${stock.quantite || stock.quantite_disponible || stock.stock_disponible || 0} ${stock.produit_unite || ""}`;
  };

  const getProduitUtiliseLabel = (stockId) => {
    const stock = stocksDisponibles.find(s => s.id === parseInt(stockId));
    if (!stock) return "Non sélectionné";
    return `${stock.produit_nom} (${stock.quantite || stock.quantite_disponible || stock.stock_disponible || 0} ${stock.produit_unite || ""} disponible)`;
  };

  const handleCancelConfirm = () => {
    setShowConfirmModal(false);
  };

  const isFormValid = () => {
    if (!formData.troupeau) return false;
    if (!formData.animal) return false;
    if (!formData.type_intervention || !formData.type_intervention.trim()) return false;
    if (!formData.date) return false;
    return true;
  };

  // Filtrer les animaux selon le troupeau sélectionné
  const animauxFiltres = formData.troupeau
    ? animaux.filter(animal => {
        const troupeauId = typeof animal.troupeau === 'object' 
          ? animal.troupeau?.id 
          : animal.troupeau;
        return troupeauId === parseInt(formData.troupeau);
      })
    : [];

  const getAnimalMatricule = () => {
    const animal = animaux.find(a => a.id === formData.animal);
    return animal ? animal.matricule : t.notProvided;
  };

  return (
    <div className="ajouter-sante-page">
      <div className="title-badge">
        <FaSyringe className="page-title-icon" aria-hidden="true" />
        <span>{t.addHealthCard}</span>
      </div>

      <form onSubmit={handleSubmit} className="ajouter-sante-form">
        {/* Bannière d'information */}
        <div className="form-info-banner">
          <span className="info-icon">ℹ️</span>
          <div className="info-content">
            <strong>{t.requiredFields}</strong>
            <span className="required-indicator">*</span> {t.requiredFieldNote}
          </div>
        </div>

        <div className="form-section-container">
          <div className="form">
            <label htmlFor="troupeau">
              {t.herd} <span className="required-indicator">*</span>
            </label>
            <select
              id="troupeau"
              name="troupeau"
              value={formData.troupeau || ""}
              onChange={handleChange}
              onBlur={handleBlur}
              className={fieldErrors.troupeau && touchedFields.troupeau ? "input-error" : ""}
            >
              <option value="">{t.selectHerd}</option>
              {troupeaux.map((troupeau) => (
                <option key={troupeau.id} value={troupeau.id}>
                  {troupeau.nom}
                </option>
              ))}
            </select>
            {fieldErrors.troupeau && touchedFields.troupeau && (
              <span className="field-error-message">{fieldErrors.troupeau}</span>
            )}
          </div>

          <div className="form">
            <label htmlFor="animal">
              {t.animal} <span className="required-indicator">*</span>
            </label>
            <select
              id="animal"
              name="animal"
              value={formData.animal || ""}
              onChange={handleChange}
              onBlur={handleBlur}
              className={fieldErrors.animal && touchedFields.animal ? "input-error" : ""}
              disabled={!formData.troupeau}
            >
              <option value="">
                {formData.troupeau ? t.selectAnimal : t.selectHerdFirst}
              </option>
              {animauxFiltres.map((animal) => (
                <option key={animal.id} value={animal.id}>
                  {animal.matricule}
                </option>
              ))}
            </select>
            {fieldErrors.animal && touchedFields.animal && (
              <span className="field-error-message">{fieldErrors.animal}</span>
            )}
          </div>

          <div className="form">
            <label htmlFor="type_intervention">
              {t.interventionTypeLabel} <span className="required-indicator">*</span>
            </label>
            <select
              id="type_intervention"
              name="type_intervention"
              value={formData.type_intervention}
              onChange={handleChange}
              onBlur={handleBlur}
              className={fieldErrors.type_intervention && touchedFields.type_intervention ? "input-error" : ""}
            >
              <option value="">{t.selectType}</option>
              <option value="vaccination">{t.vaccination}</option>
              <option value="traitement">{t.treatment}</option>
              <option value="vermifuge">{t.deworming}</option>
              <option value="autre">{t.other}</option>
            </select>
            {fieldErrors.type_intervention && touchedFields.type_intervention && (
              <span className="field-error-message">{fieldErrors.type_intervention}</span>
            )}
          </div>

          <div className="form">
            <label htmlFor="statut">{t.statusLabel}</label>
            <select
              id="statut"
              name="statut"
              value={formData.statut}
              onChange={handleChange}
            >
              <option value="traite">{t.treated}</option>
              <option value="non_traite">{t.notTreated}</option>
            </select>
          </div>

          <div className="form">
            <label htmlFor="date">
              Date <span className="required-indicator">*</span>
            </label>
            <input
              type="date"
              id="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              onBlur={handleBlur}
              className={fieldErrors.date && touchedFields.date ? "input-error" : ""}
            />
            {fieldErrors.date && touchedFields.date && (
              <span className="field-error-message">{fieldErrors.date}</span>
            )}
          </div>

          {/* Section Produits utilisés */}
          <div className="form full-width">
            <label>
              {t.productsUsed}
            </label>
            <div className="produits-utilises-container">
              {produitsUtilises.map((produit, index) => (
                <div key={index} className="produit-utilise-row">
                  <select
                    value={produit.stock_id || ""}
                    onChange={(e) => handleProduitChange(index, "stock_id", e.target.value)}
                    className="produit-select"
                  >
                    <option value="">{t.selectStock}</option>
                    {stocksDisponibles.map((stock) => (
                      <option key={stock.id} value={stock.id}>
                        {stock.produit_nom} - {stock.quantite || stock.quantite_disponible || stock.stock_disponible || 0} {stock.produit_unite || ""}
                      </option>
                    ))}
                  </select>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    placeholder={t.quantityLabel}
                    value={produit.quantite_utilisee || ""}
                    onChange={(e) => handleProduitChange(index, "quantite_utilisee", e.target.value)}
                    className="quantite-input"
                  />
                  <input
                    type="text"
                    placeholder={t.notesOptional}
                    value={produit.notes || ""}
                    onChange={(e) => handleProduitChange(index, "notes", e.target.value)}
                    className="notes-input"
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveProduit(index)}
                    className="remove-produit-button"
                    aria-label="Supprimer ce produit"
                  >
                    <FaTrash />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={handleAddProduit}
                className="add-produit-button"
              >
                <FaPlus />
                <span>Ajouter un produit</span>
              </button>
            </div>
          </div>

          <div className="form full-width">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description || ""}
              onChange={handleChange}
              placeholder="Description de l'intervention"
              rows="3"
            ></textarea>
          </div>

          <div className="form">
            <label htmlFor="prochaine_intervention">Prochaine intervention</label>
            <input
              type="date"
              id="prochaine_intervention"
              name="prochaine_intervention"
              value={formData.prochaine_intervention || ""}
              onChange={handleChange}
            />
          </div>
        </div>

        {/* Boutons d'action */}
        <div className="actions">
          <button
            type="submit"
            className="save-but"
            disabled={!isFormValid() || loading}
          >
            <FaCheck />
            <span>{loading ? t.saving : t.save}</span>
          </button>
          <button
            type="button"
            onClick={() => navigate("/sante")}
            className="cancel-but"
            disabled={loading}
          >
            <FaTimes />
            <span>{t.cancel}</span>
          </button>
        </div>
      </form>

      {/* Modal de confirmation */}
      {showConfirmModal && (
        <div className="confirm-modal-overlay" onClick={handleCancelConfirm}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-modal-header">
              <FaCheck className="confirm-modal-icon" />
              <h3>Confirmer l'ajout</h3>
            </div>
            <div className="confirm-modal-body">
              <p className="confirm-intro">
                Veuillez vérifier les informations avant de confirmer l'ajout :
              </p>
              <div className="confirm-summary">
                <div className="summary-row">
                  <span className="summary-label">Troupeau :</span>
                  <span className="summary-value">
                    {formData.troupeau 
                      ? troupeaux.find(t => t.id === parseInt(formData.troupeau))?.nom || "Non renseigné"
                      : "Non renseigné"}
                  </span>
                </div>
                <div className="summary-row">
                  <span className="summary-label">Animal :</span>
                  <span className="summary-value">{getAnimalMatricule()}</span>
                </div>
                <div className="summary-row">
                  <span className="summary-label">Type d'intervention :</span>
                  <span className="summary-value">{formData.type_intervention || "Non renseigné"}</span>
                </div>
                <div className="summary-row">
                  <span className="summary-label">Statut :</span>
                  <span className="summary-value">{formData.statut === "traite" ? "Traité" : "Non Traité"}</span>
                </div>
                <div className="summary-row">
                  <span className="summary-label">Date :</span>
                  <span className="summary-value">{formData.date || "Non renseigné"}</span>
                </div>
                {produitsUtilises.length > 0 && (
                  <div className="summary-row summary-products">
                    <span className="summary-label">Produits utilisés :</span>
                    <div className="summary-products-list">
                      {produitsUtilises.map((produit, index) => (
                        <div key={index} className="summary-product-item">
                          <span className="summary-product-name">
                            {getProduitUtiliseLabel(produit.stock_id)}
                          </span>
                          <span className="summary-product-quantity">
                            Quantité : {produit.quantite_utilisee || "Non renseigné"} 
                            {produit.stock_id && stocksDisponibles.find(s => s.id === parseInt(produit.stock_id))?.produit_unite && 
                              ` ${stocksDisponibles.find(s => s.id === parseInt(produit.stock_id))?.produit_unite}`
                            }
                          </span>
                          {produit.notes && (
                            <span className="summary-product-notes">
                              Notes : {produit.notes}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {produitsUtilises.length === 0 && (
                  <div className="summary-row">
                    <span className="summary-label">Produits utilisés :</span>
                    <span className="summary-value">Aucun produit</span>
                  </div>
                )}
                {formData.description && (
                  <div className="summary-row">
                    <span className="summary-label">Description :</span>
                    <span className="summary-value">{formData.description}</span>
                  </div>
                )}
                {formData.prochaine_intervention && (
                  <div className="summary-row">
                    <span className="summary-label">Prochaine intervention :</span>
                    <span className="summary-value">{formData.prochaine_intervention}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="confirm-modal-footer">
              <button
                type="button"
                onClick={handleCancelConfirm}
                className="confirm-modal-cancel"
                disabled={loading}
              >
                <FaTimes />
                <span>{t.cancel}</span>
              </button>
              <button
                type="button"
                onClick={handleConfirmSubmit}
                className="confirm-modal-confirm"
                disabled={loading}
              >
                <FaCheck />
                <span>{loading ? t.saving : t.confirm}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
}
